import React, { useState, useEffect, useCallback } from "react";
import { fetchOrders, updateItemStatus } from "../services/api";

// Waiter "ready to run" view: items the kitchen / bar / café have marked
// READY, grouped by table, with one tap to mark them served. Polls so it
// stays current as stations finish items.
const STATION_ICON = { kitchen: "🍳", bar: "🍺", cafe: "☕" };

export default function RunViewPage() {
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchOrders({ activeOnly: true, todayOnly: true });
      setOrders(data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  const serve = async (orderId, itemId) => {
    setBusy(itemId);
    try {
      const updated = await updateItemStatus(orderId, itemId, "served");
      setOrders((cur) => cur.map((o) => (o.id === updated.id ? updated : o)));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update item.");
    } finally {
      setBusy(null);
    }
  };

  // Group ready items by table.
  const groups = {};
  for (const o of orders) {
    for (const it of o.items || []) {
      if (it.status !== "ready") continue;
      const key = o.table_number ?? o.table_id ?? "—";
      (groups[key] = groups[key] || []).push({ o, it });
    }
  }
  const tables = Object.keys(groups);

  return (
    <>
      <h1>🛎️ Ready to run</h1>
      <p className="muted" style={{ maxWidth: 560 }}>
        Items the kitchen, bar, and café have marked ready. Deliver them to the table and tap “Served”.
      </p>
      {err && <div className="error">{err}</div>}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : tables.length === 0 ? (
        <p className="muted">Nothing waiting to run right now. 🎉</p>
      ) : (
        <div className="grid-cards">
          {tables.map((tk) => (
            <div key={tk} className="card">
              <div className="space-between" style={{ marginBottom: 6 }}>
                <h3 style={{ margin: 0 }}>Table {tk}</h3>
                <button
                  className="btn small"
                  onClick={() => groups[tk].forEach(({ o, it }) => serve(o.id, it.id))}
                >
                  Serve all
                </button>
              </div>
              <ul>
                {groups[tk].map(({ o, it }) => (
                  <li
                    key={it.id}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}
                  >
                    <span>
                      {STATION_ICON[it.station] || ""} <b>{it.quantity}×</b> {it.name || `#${it.menu_id}`}
                      {o.round_number ? <span className="muted"> · R{o.round_number}</span> : null}
                    </span>
                    <button className="btn small" disabled={busy === it.id} onClick={() => serve(o.id, it.id)}>
                      {busy === it.id ? "…" : "Served"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
